import React, { useRef, useState, useCallback } from "react";

/**
 * Tone curve editor — Master/R/G/B point curves on an SVG grid.
 * Click empty space = add point · drag = move · double-click point = remove.
 * Points are stored on node.ops.curves.{master,r,g,b} as [{x,y}] in [0,1],
 * baked to 1D LUTs by both engines + the compiler (same interp).
 */
const SIZE = 224;
const CHANNELS = [
  { key: "master", label: "M", color: "#e7e5e4" },
  { key: "r", label: "R", color: "#f87171" },
  { key: "g", label: "G", color: "#4ade80" },
  { key: "b", label: "B", color: "#60a5fa" },
];

const toSvg = (p) => ({ cx: p.x * SIZE, cy: (1 - p.y) * SIZE });

export const CurveEditor = ({ curves = {}, onChange }) => {
  const [ch, setCh] = useState("master");
  const [dragIdx, setDragIdx] = useState(-1);
  const svgRef = useRef(null);

  const pts = curves[ch] || [];
  const sorted = [...pts].sort((a, b) => a.x - b.x);
  const channel = CHANNELS.find((c) => c.key === ch);

  const eventPoint = (e) => {
    const r = svgRef.current.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)),
      y: Math.min(1, Math.max(0, 1 - (e.clientY - r.top) / r.height)),
    };
  };

  const setPts = (next) => onChange({ ...curves, [ch]: next });

  const onDown = (e) => {
    if (e.target.dataset.pt !== undefined) { setDragIdx(parseInt(e.target.dataset.pt, 10)); return; }
    const p = eventPoint(e);
    // seed endpoints on first edit so the curve stays anchored
    const base = pts.length ? pts : [{ x: 0, y: 0 }, { x: 1, y: 1 }];
    const next = [...base, p];
    setPts(next);
    setDragIdx(next.length - 1);
  };

  const onMove = useCallback((e) => {
    if (dragIdx < 0) return;
    const p = eventPoint(e);
    const next = [...pts];
    next[dragIdx] = p;
    setPts(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragIdx, pts]);

  const polyline = (sorted.length >= 2 ? sorted : [{ x: 0, y: 0 }, { x: 1, y: 1 }])
    .map((p) => { const { cx, cy } = toSvg(p); return `${cx},${cy}`; }).join(" ");

  return (
    <div className="space-y-2" data-testid="grade-curve-editor">
      <div className="flex items-center gap-1">
        {CHANNELS.map((c) => (
          <button key={c.key} onClick={() => setCh(c.key)}
            data-testid={`curve-ch-${c.key}`}
            className={`w-7 h-6 rounded text-[10px] font-bold border transition-colors ${
              ch === c.key ? "border-orange-500 text-orange-300" : "border-white/10 text-white/40 hover:text-white"}`}
            style={ch === c.key ? {} : { color: c.color + "88" }}>
            {c.label}
          </button>
        ))}
        <button onClick={() => setPts([])}
          data-testid="curve-reset-btn"
          className="ml-auto text-[10px] font-mono text-white/30 hover:text-red-400">RESET</button>
      </div>
      <svg ref={svgRef} width="100%" viewBox={`0 0 ${SIZE} ${SIZE}`}
        className="bg-black/60 rounded-lg border border-white/10 cursor-crosshair touch-none select-none"
        onMouseDown={onDown} onMouseMove={onMove}
        onMouseUp={() => setDragIdx(-1)} onMouseLeave={() => setDragIdx(-1)}>
        {[0.25, 0.5, 0.75].map((g) => (
          <g key={g} stroke="#ffffff14">
            <line x1={g * SIZE} y1="0" x2={g * SIZE} y2={SIZE} />
            <line x1="0" y1={g * SIZE} x2={SIZE} y2={g * SIZE} />
          </g>
        ))}
        <line x1="0" y1={SIZE} x2={SIZE} y2="0" stroke="#ffffff22" strokeDasharray="4 4" />
        <polyline points={polyline} fill="none" stroke={channel.color} strokeWidth="1.6" />
        {pts.map((p, i) => {
          const { cx, cy } = toSvg(p);
          return <circle key={i} data-pt={i} cx={cx} cy={cy} r="5"
            fill={channel.color} stroke="#000" strokeWidth="1.5" className="cursor-grab"
            onDoubleClick={(e) => { e.stopPropagation(); setPts(pts.filter((_, k) => k !== i)); }} />;
        })}
      </svg>
      <p className="text-[9px] text-white/25">click = add · drag = move · dbl-click = remove</p>
    </div>
  );
};
